import React from 'react';
import {
    Flex,
    ReadMore,
    List,
    useStyles,
    pxToRem,
    pxArrayToRem,
    Grid,
    useAppDisplayModeContext,
    ProfileNavigation,
    useTheme,
} from '@archipro-design/aria';
import * as S from './Content.style';
import { useIsSafari } from '@modules/root/hook/use-is-safari';
import { ArrowRightLine } from '@archipro-design/icons';
import Stats from './Stats';

import CompanyInfoStats from './CompanyInfoStats';
import { useTracker } from '@archipro-website/tracker';
import { fireProfessionalLinkEvent } from '../../util/tracker-helpers';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';
import { useLoaderData } from '@remix-run/react';
import type { ProjectPageLoader } from '@modules/project/type';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

const MAX_LINES = 15;

interface ContentProps {
    data: ProjectPageLoader['project']['Professional'];
}

interface ListProps {
    label: string;
    link: string;
}

const Content = ({ data }: ContentProps) => {
    const [visible, setVisible] = React.useState(false);
    const isSafari = useIsSafari();
    const { css } = useStyles({ isSafari, visible });
    const theme = useTheme();
    const { siteVariables } = useTheme();
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    const links = generateLinks(data, mobile);
    const { project } = useLoaderData<typeof loader>();
    const clickProxy = useWebsiteTrackingProxy({
        professionalID: project.Professional.ID,
        professionalTitle: project.Professional.Title,
        itemType: 'Project',
    });

    return (
        <Flex column={false} vAlign="center">
            <Grid
                columns={pxArrayToRem([880, 896])}
                className={css({
                    columnGap: pxToRem(44),
                })}
            >
                {mobile ? (
                    <ProfileNavigation
                        className={css(S.Tabs)}
                        items={links.map((link) => ({
                            as: 'a',
                            href: link.link,
                            children: link.label,
                            outlined: true,
                            color: 'white',
                            key: link.label,
                        }))}
                    />
                ) : (
                    <div>
                        <List
                            onMouseEnter={() => setVisible(true)}
                            onMouseLeave={() => setVisible(false)}
                            items={links.map((link) => {
                                return {
                                    ...link,
                                    iconAfter: <ArrowRightLine />,
                                    onClick: () => {
                                        fireProfessionalLinkEvent(
                                            link.label,
                                            window.location.href,
                                            tracker
                                        );
                                    },
                                };
                            })}
                            className={css(S.LinkContainer)}
                            variables={{
                                listBorderColor:
                                    siteVariables.colors.primitive.black,
                            }}
                        />
                        <Stats data={data} />
                    </div>
                )}

                <div className={css(S.AboutTextContainer)}>
                    <ReadMore
                        content={{
                            children: (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: data.BlockSummary ?? '',
                                    }}
                                    onClick={clickProxy}
                                />
                            ),
                        }}
                        maxLines={MAX_LINES}
                        className={css(S.AboutTextContent)}
                        variables={{
                            toggleFontSize: pxToRem(16),
                            contentLineHeight:
                                theme.siteVariables.lineHeightSmall,
                        }}
                    />
                    <CompanyInfoStats data={data} />
                </div>
            </Grid>
        </Flex>
    );
};

const generateLinks = (
    data: ProjectPageLoader['project']['Professional'],
    mobile: boolean
): ListProps[] => {
    const BASE_COMPANY_URL = `/professional/${data.URLSegment}`;
    return [
        {
            label: mobile ? 'Home' : 'Overview',
            link: BASE_COMPANY_URL,
        },
        {
            label: 'About',
            link: `${BASE_COMPANY_URL}/info`,
        },
        ...(data.HasProducts
            ? [
                  {
                      label: 'Products',
                      link: `${BASE_COMPANY_URL}/our-products`,
                  },
              ]
            : []),
        {
            label: 'Projects',
            link: `${BASE_COMPANY_URL}/case-studies`,
        },
        ...(data.HasArticles
            ? [
                  {
                      label: 'Articles',
                      link: `${BASE_COMPANY_URL}/journal`,
                  },
              ]
            : []),
        ...(data.HasVideos
            ? [
                  {
                      label: 'Videos',
                      link: `${BASE_COMPANY_URL}/videos`,
                  },
              ]
            : []),
        ...(data.HasPartners
            ? [
                  {
                      label: 'Network',
                      link: `${BASE_COMPANY_URL}/partners`,
                  },
              ]
            : []),
        {
            label: 'Contact',
            link: `${BASE_COMPANY_URL}/contact`,
        },
    ];
};

export default Content;
